/* eslint-disable @typescript-eslint/dot-notation */
import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { Location } from '@core/interfaces/location';
import { VEGuidedInput } from '@core/interfaces/modal-inputs/ve-guided-input';
import { VisualEvent } from '@core/interfaces/visual-event';
import { CameraSource } from '@capacitor/camera';
import { CameraService } from 'src/app/native/services/camera.service';
import { VisualEventsImagesService } from '@mobile/services/visual-events-images.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { VisualEventRadioOption } from '@shared/interfaces/radio-option';
import { RadioSelectionForm } from '@shared/interfaces/radio-selection-form';
import { SelectionField } from '@shared/interfaces/selection-field';
import { TextField } from '@shared/interfaces/text-field';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogService, FULL_SCREEN_DIALOG, FULL_SCREEN_DIALOG_CLASS, SnackBarService } from 'osc-reference-app-common';
import { VEStatus } from '@interactive-isometric-shared/enums/ve-status';
import { LoggerService } from '@shared/services/logger.service';
import { ComponentType } from '@angular/cdk/portal';
import { concat, finalize, firstValueFrom, Subject, switchMap, take, takeUntil } from 'rxjs';
import { NativeKeyboardService } from '@shared/services/native-keyboard.service';
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { SelectionFieldComponent } from '@shared/components/selection-field/selection-field.component';
import { TextFieldComponent } from '@shared/components/text-field/text-field.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { OfflineImagePipe } from '@shared/pipes/offline-image.pipe';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { AddButtonActionItem } from '@shared/interfaces/add-button-action-item';
import { AddButtonComponent } from '@shared/components/add-button/add-button.component';
import { PhotoSelectionDialogComponent } from '@shared/components/photo-selection-dialog/photo-selection-dialog.component';
import {
  PhotoSelectionDialogInput,
  PhotoSelectionDialogResult,
  PhotoSelectionInputItem,
} from '@shared/components/photo-selection-dialog/photo-selection-dialog-input';
import { FullscreenGalleryInput } from '@shared/components/fullscreen-gallery/fullscreen-gallery-input';
import { FullscreenGalleryDialogComponent } from '@shared/components/fullscreen-gallery/fullscreen-gallery-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VisualEventsService } from 'src/app/mobile/services/visual-events.service';
import { VisualEventGuidedService } from './visual-event-guided.service';

@Component({
  selector: 'app-visual-event-guided',
  templateUrl: './visual-event-guided.component.html',
  styleUrls: ['./visual-event-guided.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,
    NgClass,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    SelectionFieldComponent,
    TextFieldComponent,
    NgStyle,
    MatMiniFabButton,
    MatCheckbox,
    AsyncPipe,
    TranslateModule,
    OfflineImagePipe,
    AddButtonComponent,
  ],
})
export class VisualEventGuidedComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  readonly #logger = inject(LoggerService);
  readonly #visualEventsService = inject(VisualEventsService);
  readonly #translate = inject(TranslateService);
  readonly #cameraService = inject(CameraService);
  readonly #visualEventsImagesService = inject(VisualEventsImagesService);
  readonly #formBuilder = inject(FormBuilder);
  readonly #snackBarService = inject(SnackBarService);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #spinnerService = inject(SpinnerService);
  readonly #dialogService = inject(DialogService);
  readonly #nativeKeyboardService = inject(NativeKeyboardService);
  readonly #visualEventGuidedService = inject(VisualEventGuidedService);
  readonly #destroyRef = inject(DestroyRef);

  @Input({ required: true }) public data: VEGuidedInput;
  @Output() public readonly dataChange = new EventEmitter<VEGuidedInput>();
  @Output() public readonly closeWindow = new EventEmitter<void>();
  @Output() public readonly addVELocation = new EventEmitter<VisualEvent>();
  @Output() public readonly refreshVELocation = new EventEmitter<VisualEvent[]>();
  public currentState: 'high' | 'low' | 'form' | 'location' = 'high';
  public currentHighIndex = -1;
  public currentLowIndex = -1;
  public isHorizontal = false;
  public currentVETypes: VisualEventRadioOption[];
  public isPictureAddInProgress = false;
  public currentVE: VisualEvent;
  public radioSelectionForVisualEventStatus: RadioSelectionForm;
  public radioSelectionForPriorityCode: RadioSelectionForm;
  public visualEventNotesField = 'visualEventNotesField';
  public visualEventRecommendationField = 'visualEventRecommendationField';

  public visualEventStatus: SelectionField;
  public visualEventPriorityCode: SelectionField;
  public visualEventNotes: TextField;
  public visualEventLocations: Location[] = [];
  public visualEventWholeEquipment = false;
  public visualEventRecommendation: TextField;

  public visualEventGuidedForm: FormGroup;
  public assignLocationLabel: string;
  public pictureLabel: string;
  public isSaving: boolean;
  public addPhotoActions: AddButtonActionItem[] = [
    {
      text: this.#translate.instant('VisualEventGuided.takePhoto'),
      icon: 'icons-add-photo',
      action: () => this.addPhoto(),
    },
    {
      text: this.#translate.instant('VisualEventGuided.addPhotos'),
      icon: 'icons-images',
      action: () => this.onAddPhotosClicked(),
    },
  ];

  #destroy$ = new Subject<void>();

  public get isPriorityRequired(): boolean {
    const selectedVisualEventStatus = this.data.visualEventStatus.find(v => v.label === this.visualEventStatus?.value);
    return selectedVisualEventStatus?.needsPriority ?? false;
  }

  public ngAfterViewInit(): void {
    this.initializeVisualGuidedForm();
  }

  public ngOnInit(): void {
    this.refreshVEGuidedList();

    this.#nativeKeyboardService.autoScrollToElementIfHiddenByKeyboard().pipe(takeUntil(this.#destroy$)).subscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.refreshVEGuidedList();
    }
  }

  public ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  public refreshVEGuidedList(): void {
    this.data.visualEventTypes.forEach((itm, index) => {
      itm['completeCount'] = this.isSectionComplete(index);
    });
  }

  public padLeft(text: string, padChar: string, size: number): string {
    return (String(padChar).repeat(size) + text).substr(size * -1, size);
  }

  public updateVELocation(event: VisualEvent): void {
    const currentVe = this.data.visualEvents.find(e => e.eventId === event.eventId);
    if (!currentVe) {
      return;
    }
    currentVe.locations = event.locations;
    this.visualEventLocations = event.locations;
  }

  public close(): void {
    this.closeWindow.emit();
  }

  public addNewVELocation(): void {
    this.addVELocation.emit(this.currentVE);
  }

  public isSectionComplete(index: number) {
    let completeCount = 0;
    this.data.visualEventTypes[index].options.forEach(VEType => {
      VEType['isComplete'] = this.data.visualEvents.findIndex(ve => ve.visualEventType === VEType.id);
      completeCount += VEType['isComplete'] > -1 ? 1 : 0;
    });
    return completeCount;
  }

  public startSection(index: number) {
    this.currentHighIndex = index;
    this.currentState = 'low';
    this.currentVETypes = this.data.visualEventTypes[index].options;
  }

  public markSectionNA(index: number) {
    const veRequests: Promise<any>[] = [];
    const newVE: VisualEvent[] = [];
    this.data.visualEventTypes[index]['isSaving'] = true;
    this.data.visualEventTypes[index].options.forEach(item => {
      const visualEvent = {
        visualEventType: item.id,
        visualEventStatus: 'N/A',
        visualEventRecommendation: '',
        taskId: this.data.taskId,
        description: '',
        images: [],
        isPhotoOnlyEvent: false,
        locations: [],
        wholeEquipment: false,
      };
      newVE.push(visualEvent);
      veRequests.push(firstValueFrom(this.#visualEventsService.saveVisualEvent(visualEvent, this.data.taskId)));
    });
    Promise.all(veRequests)
      .then(res => {
        res.forEach((id, i) => {
          newVE[i].eventId = id;
        });
        this.data.visualEvents.push(...newVE);
        this.data.visualEventTypes[index].options.forEach((veType, i) => {
          veType['isComplete'] = this.data.visualEvents.length - newVE.length + i;
        });
        this.data.visualEventTypes[index]['isSaving'] = false;
        this.data.visualEventTypes[index]['completeCount'] = this.padLeft(`${this.data.visualEventTypes[index].options.length}`, '0', 2);
        this.#changeDetectorRef.detectChanges();
      })
      .catch(err => {
        this.#logger.error('veRequests failed', err);
      });
  }

  public startVisualEvent(index: number) {
    this.currentLowIndex = index;
    this.currentState = 'form';
    this.setVEInfo(this.currentVETypes[index].isComplete > -1 ? this.data.visualEvents[this.currentVETypes[index].isComplete] : undefined);
  }

  public markVE_Na(index: number) {
    this.data.visualEventTypes[this.currentHighIndex].options[index]['isSaving'] = true;
    const newVE: VisualEvent = {
      visualEventType: this.data.visualEventTypes[this.currentHighIndex].options[index].id,
      visualEventStatus: 'N/A',
      visualEventRecommendation: '',
      taskId: this.data.taskId,
      description: '',
      images: [],
      isPhotoOnlyEvent: false,
      locations: [],
      wholeEquipment: false,
    };
    firstValueFrom(this.#visualEventsService.saveVisualEvent(newVE, this.data.taskId))
      .then(res => {
        newVE.eventId = res;
        const newIndex = this.data.visualEvents.push(newVE) - 1;
        this.data.visualEventTypes[this.currentHighIndex].options[index]['isSaving'] = false;
        this.data.visualEventTypes[this.currentHighIndex].options[index]['isComplete'] = newIndex;
        this.data.visualEventTypes[this.currentHighIndex]['completeCount'] += 1;
        this.#changeDetectorRef.detectChanges();
      })
      .catch(err => {
        this.#logger.error('saveVisualEvent failed', err);
      });
  }

  public setNotes(note: string) {
    this.currentVE.description = note;
    this.saveVE();
  }

  public setRecommendation(recommendation: string) {
    this.currentVE.visualEventRecommendation = recommendation;
    this.saveVE();
  }

  public assignLocation() {
    this.currentState = 'location';
  }

  public async addPhoto() {
    this.isPictureAddInProgress = true;

    const photo = await this.#cameraService.pickPhoto({ from: CameraSource.Camera, withDimensions: true });
    if (!photo) {
      this.isPictureAddInProgress = false;
      this.#changeDetectorRef.detectChanges();
      return;
    }
    this.#visualEventsImagesService
      .addPhoto({
        projectId: this.data.projectId,
        jobId: this.data.jobId,
        taskId: this.data.taskId,
        photo,
        eventId: this.currentVE.eventId,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.isPictureAddInProgress = false;
          this.#changeDetectorRef.detectChanges();
        }),
      )
      .subscribe({
        next: visualEvent => {
          this.data.allVEData.push(visualEvent); // Required to specify that the image is part of the gallery.
          this.currentVE.images.push(visualEvent.images[0]);
          this.saveVE();
        },
        error: err => {
          this.#logger.error('Failed to add gallery photo', err);
          this.#snackBarService.error(this.#translate.instant('VisualEventGuided.addPhotoError'));
        },
      });
  }

  public expand(index: number): void {
    this.openFullscreen(index);
  }

  public openFullscreen(index: number): void {
    const galleryInput: FullscreenGalleryInput = {
      images: this.currentVE.images.map(img => ({ src: img.imageUrl })),
      initialIndex: index,
    };

    this.#dialogService.openDialogWithCustomStyles(
      FullscreenGalleryDialogComponent as ComponentType<FullscreenGalleryDialogComponent>,
      galleryInput,
      FULL_SCREEN_DIALOG_CLASS,
      FULL_SCREEN_DIALOG,
    );
  }

  public deleteImage(id: string): void {
    this.unlinkImage(id);
  }

  public async onAddPhotosClicked(): Promise<void> {
    const dialogInput: PhotoSelectionDialogInput = {
      photos: this.getGalleryPhotosForSelection(),
    };
    this.isPictureAddInProgress = true;

    const dialogRef = this.#dialogService.openFullScreenDialog<
      PhotoSelectionDialogComponent,
      PhotoSelectionDialogInput,
      PhotoSelectionDialogResult
    >(PhotoSelectionDialogComponent as ComponentType<PhotoSelectionDialogComponent>, dialogInput);

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        takeUntil(this.#destroy$),
        switchMap(async result => {
          if (!result) {
            return;
          }

          if (result.addedPhotos?.length) {
            await this.linkPhotosToVisualEvent(result.addedPhotos);
          }

          if (result.removedPhotos?.length) {
            this.unlinkPhotosFromVisualEvent(result.removedPhotos);
          }
        }),
        finalize(() => {
          this.isPictureAddInProgress = false;
          this.#changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();
  }

  public backClicked() {
    switch (this.currentState) {
      case 'form':
        this.currentState = 'low';
        break;
      case 'high':
        break;
      case 'location':
        this.visualEventLocations = this.currentVE.wholeEquipment ? [] : this.currentVE.locations;
        this.visualEventWholeEquipment = this.currentVE.wholeEquipment ?? false;
        this.currentState = 'form';
        this.saveVE();
        break;
      case 'low':
        break;
    }
  }

  public addLocation() {
    if (this.currentVE.locations === undefined) {
      this.currentVE.locations = [];
    }
    this.currentVE.locations.push({ left: 100, top: 100, eventId: this.currentVE.eventId, fileId: this.data.metadataId });
  }

  public nextClicked() {
    if (this.currentState === 'form') {
      if (this.visualEventGuidedForm.valid) {
        if (
          this.visualEventStatus.value === VEStatus.Fail ||
          this.visualEventStatus.value === VEStatus.FurtherInspect ||
          this.visualEventStatus.value === VEStatus.NotAcceptable
        ) {
          const check = this.validationForVisualEventFailedStatus();
          if (check) {
            this.nextAction();
          } else {
            this.#snackBarService.error(this.#translate.instant('manageVisualEvent.visualGuidedGeneralErrorMsg'));
          }
        } else {
          this.nextAction();
        }
      } else {
        this.#snackBarService.error(this.#translate.instant('manageVisualEvent.visualGuidedGeneralErrorMsg'));
      }
    } else {
      this.nextAction();
    }
  }

  public async saveVE() {
    this.isSaving = true;
    this.visualEventGuidedForm.controls.vEStatus.setValue(this.visualEventStatus.value);
    this.visualEventGuidedForm.controls.vEPriorityCode.setValue(this.visualEventPriorityCode.id);
    this.currentVE.visualEventStatus = this.visualEventStatus.value;
    this.currentVE.visualEventPriority = this.visualEventPriorityCode.id;
    if (
      this.visualEventStatus.value === VEStatus.Fail ||
      this.visualEventStatus.value === VEStatus.FurtherInspect ||
      this.visualEventStatus.value === VEStatus.NotAcceptable
    ) {
      this.setLabelForVisualEventFailedStatus();
    } else {
      this.setLabelForVisualEventPassStatus();
    }
    this.setVEInfo(this.currentVE);
    if (this.data.visualEventTypes[this.currentHighIndex].options[this.currentLowIndex]['isComplete'] < 0) {
      if (this.currentVE.visualEventStatus !== '') {
        this.#spinnerService.show(this.#spinnerService.mainSpinnerId);
        await firstValueFrom(this.#visualEventsService.saveVisualEvent(this.currentVE, this.data.taskId))
          .then(res => {
            this.currentVE.eventId = res;
            const newIndex = this.data.visualEvents.push(this.currentVE) - 1;
            this.data.visualEventTypes[this.currentHighIndex].options[this.currentLowIndex]['isComplete'] = newIndex;
            this.data.visualEventTypes[this.currentHighIndex]['completeCount'] += 1;
            this.isSaving = false;
            this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
            this.#changeDetectorRef.detectChanges();
          })
          .catch(err => {
            this.isSaving = false;
            this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
            this.#logger.error('saveVisualEvent failed', err);
          });
      } else {
        this.isSaving = false;
      }
    } else {
      await firstValueFrom(this.#visualEventsService.updateVisualEvent(this.currentVE.eventId, this.currentVE, this.data.taskId))
        .then(res => {
          this.refreshVELocation.emit(this.data.visualEvents);
        })
        .catch(err => {
          this.#logger.error('updateVisualEvent failed', err);
        });
      this.isSaving = false;
    }
    this.#changeDetectorRef.detectChanges();
  }

  private unlinkImage(fileId: string): void {
    this.#visualEventGuidedService
      .unlinkImageFromVisualEvent(this.currentVE, fileId, this.data.taskId)
      .pipe(take(1), takeUntil(this.#destroy$))
      .subscribe({
        next: () => {
          this.#changeDetectorRef.detectChanges();
          this.refreshVELocation.emit(this.data.visualEvents);
          this.#snackBarService.success(this.#translate.instant('VisualEventGuided.unlinkPhotoSuccess'));
        },
        error: err => {
          this.#logger.error('Failed to unlink image', err);
          this.#snackBarService.error(this.#translate.instant('VisualEventGuided.unlinkPhotoError'));
        },
      });
  }

  private getGalleryPhotosForSelection(): PhotoSelectionInputItem[] {
    const data = this.data;
    if (!data) {
      return [];
    }

    const linkedPhotoIds = new Set(this.currentVE?.images?.map(img => img.fileId) ?? []);

    return data.allVEData
      .filter(ve => ve.isPhotoOnlyEvent)
      .flatMap(ve => ve.images)
      .map(photo => ({
        ...photo,
        selected: linkedPhotoIds.has(photo.fileId),
      }));
  }

  private async linkPhotosToVisualEvent(photos: PhotoSelectionInputItem[]) {
    for (const photo of photos) {
      this.currentVE.images.push({
        fileId: photo.fileId,
        imageUrl: photo.imageUrl,
      });

      await this.saveVE(); // The save needs to occur for each photo added because of the way changes to images are handled in services.
    }
  }

  private unlinkPhotosFromVisualEvent(photos: PhotoSelectionInputItem[]): void {
    if (!photos.length) {
      return;
    }

    const unlinkOperations = photos.map(photo =>
      this.#visualEventGuidedService.unlinkImageFromVisualEvent(this.currentVE, photo.fileId, this.data.taskId).pipe(take(1)),
    );

    concat(...unlinkOperations)
      .pipe(
        takeUntil(this.#destroy$),
        finalize(() => {
          this.#changeDetectorRef.detectChanges();
          this.refreshVELocation.emit(this.data.visualEvents);
        }),
      )
      .subscribe({
        error: err => {
          this.#logger.error('Failed to unlink image', err);
          this.#snackBarService.error(this.#translate.instant('VisualEventGuided.unlinkPhotoError'));
        },
      });
  }

  private nextAction() {
    switch (this.currentState) {
      case 'form':
        if (this.currentLowIndex < this.currentVETypes.length - 1) {
          this.startVisualEvent(this.currentLowIndex + 1);
        } else {
          this.currentState = 'low';
        }
        break;
      case 'high':
        break;
      case 'location':
        break;
      case 'low':
        this.currentState = 'high';
        break;
    }
  }

  private initializeVisualGuidedForm(): void {
    this.visualEventGuidedForm = this.#formBuilder.group({
      vEStatus: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      vEPriorityCode: new FormControl<string>('', { nonNullable: true }),
    });
  }

  private filterCurrentVeData(currentVe: VisualEvent): VisualEvent {
    switch (currentVe.visualEventStatus) {
      case VEStatus.Acceptable: {
        currentVe.visualEventPriority = '';
        currentVe.visualEventRecommendation = '';
        break;
      }
      case VEStatus.NotApplicable: {
        currentVe.images = [];
        currentVe.locations = [];
        currentVe.wholeEquipment = false;
        currentVe.visualEventPriority = '';
        currentVe.visualEventRecommendation = '';
        break;
      }
      default: {
        break;
      }
    }
    return currentVe;
  }

  private setLabelForVisualEventFailedStatus(): void {
    this.visualEventNotes.label = this.#translate.instant('manageVisualEvent.notesReq');
    this.visualEventPriorityCode.label = this.#translate.instant('manageVisualEvent.priorityCodeReq');
    this.visualEventRecommendation.label = this.#translate.instant('manageVisualEvent.recommendationReq');
    this.assignLocationLabel = this.#translate.instant('manageVisualEvent.assignLocationReq');
    this.pictureLabel = this.#translate.instant('manageVisualEvent.pictureReq');
  }

  private setLabelForVisualEventPassStatus(): void {
    this.visualEventNotes.label = this.#translate.instant('manageVisualEvent.notes');
    this.visualEventPriorityCode.label = this.#translate.instant('manageVisualEvent.priorityCode');
    this.visualEventRecommendation.label = this.#translate.instant('manageVisualEvent.recommendation');
    this.assignLocationLabel = this.#translate.instant('manageVisualEvent.assignLocation');
    this.pictureLabel = '';
  }

  private validationForVisualEventFailedStatus(): boolean {
    const priorityCode = this.visualEventPriorityCode.value;
    const notes = this.visualEventNotes.value;
    const recommendation = this.visualEventRecommendation.value;
    const location = this.visualEventLocations.length > 0 || this.visualEventWholeEquipment;
    if (priorityCode && notes && recommendation && location && this.currentVE?.images.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  private setVEInfo(visualEvent?: VisualEvent) {
    this.currentVE = visualEvent ?? {
      description: '',
      images: [],
      isPhotoOnlyEvent: false,
      visualEventRecommendation: '',
      visualEventType: this.data.visualEventTypes[this.currentHighIndex].options[this.currentLowIndex].id,
      locations: [],
      wholeEquipment: false,
      taskId: this.data.taskId,
    };
    this.currentVE = this.filterCurrentVeData(this.currentVE);
    this.visualEventStatus = {
      id: 'visualEventStatus',
      label: this.#translate.instant('manageVisualEvent.visualEventStatus'),
      value: this.currentVE.visualEventStatus ?? '',
      placeholder: ``,
      required: true,
    };
    this.visualEventPriorityCode = {
      id: this.currentVE.visualEventPriority ?? '',
      label: this.#translate.instant('manageVisualEvent.priorityCode'),
      value: this.currentVE.visualEventPriority ?? '',
      placeholder: ``,
    };
    this.visualEventNotes = {
      id: 'visualEventNotes',
      label: this.#translate.instant('manageVisualEvent.notes'),
      value: this.currentVE.description ?? '',
      placeholder: ``,
      maxCountOfLines: 2,
      maxLength: 75,
    };
    this.visualEventRecommendation = {
      id: 'visualEventRecommendation',
      label: this.#translate.instant('manageVisualEvent.recommendation'),
      value: this.currentVE.visualEventRecommendation ?? '',
      placeholder: ``,
      maxCountOfLines: 2,
      maxLength: 150,
    };
    this.radioSelectionForVisualEventStatus = {
      groups: [
        {
          name: '',
          options: this.data.visualEventStatus.map(s => ({
            id: s.label,
            label: s.label,
          })),
        },
      ],
      selectedId: this.currentVE.visualEventStatus ?? '',
    };
    this.radioSelectionForPriorityCode = {
      groups: [
        {
          name: '',
          options: this.data.priorityCodes.map(p => ({
            id: p.code,
            label: p.label,
            value: p.code,
          })),
        },
      ],
      selectedId: this.currentVE.visualEventPriority ?? '',
    };
    this.visualEventWholeEquipment = this.currentVE.wholeEquipment ?? false;
    this.currentVE.locations = this.visualEventWholeEquipment ? [] : this.currentVE.locations;
    this.visualEventLocations = this.currentVE.locations ?? [];
    this.visualEventGuidedForm.controls.vEStatus.setValue(this.currentVE?.visualEventStatus ?? '');
    this.visualEventGuidedForm.controls.vEPriorityCode.setValue(this.currentVE?.visualEventPriority ?? '');
    if (
      this.currentVE?.visualEventStatus === VEStatus.Fail ||
      this.currentVE?.visualEventStatus === VEStatus.FurtherInspect ||
      this.currentVE?.visualEventStatus === VEStatus.NotAcceptable
    ) {
      this.setLabelForVisualEventFailedStatus();
    } else {
      this.setLabelForVisualEventPassStatus();
    }
  }
}
